import { useId, useState } from "react";
import {
  grupujPoWypowiedziach,
  type PowtarzaneSlowo,
  type WystapienieSlowa,
  type ZakresTrafienia,
} from "@shared/powtarzaneSlowa.js";
import { odmien } from "../../lib/polski.js";
import { IconStrzalkaDol } from "../icons/index.js";
import { Badge } from "../ui/Badge.js";
import { Collapse } from "../ui/Collapse.js";

/**
 * "Powtarzane slowa": slowa i zwroty z listy methodology/powtarzane-slowa.json, ktore mowca powtorzyl co najmniej dwa razy
 * (liczy shared/powtarzaneSlowa.ts, bez modelu). Jeden komponent dla karty oceny administratora i linku recenzenta.
 *
 * TO INFORMACJA, NIE OCENA. Dlatego kolory sa neutralne (zadnego zlota ani szarosci oceny, zadnego statusu), licznik to
 * neutralna plakietka, a zdanie nad lista mowi to wprost. Wnioski o czlowieku stawia czlowiek, nie ten widok.
 *
 * ZWIEZLE (zasada metodyki "Nie zasypujemy klienta tekstem"): domyslnie trzy najczestsze slowa, reszta za "Pokaz
 * wszystkie", a wystapienia kazdego slowa sa zwiniete pod przyciskiem ze slowem i liczba. Po rozwinieciu jedna pozycja
 * to jedna wypowiedz (przycisk "Wypowiedz N" i konteksty wszystkich trafien w niej), najpierw piec wypowiedzi.
 * Przycisk "Wypowiedz N" przewija transkrypt jak numer przy wskazaniu w karcie oceny (ta sama klasa i ten sam
 * mechanizm), a do tego zaznacza na chwile trafienia slowa w dymku i przewija do pierwszego z nich.
 */

/** Zdanie nad lista, wspolne dla karty oceny i linku recenzenta. Lista jest w metodyce; wiekszosc pozycji czeka na
 *  potwierdzenie autora metodyki, wiec zdanie nie przypisuje mu jej ukladu. */
export const OBJASNIENIE_POWTARZANYCH_SLOW =
  "To liczenie słów, a nie ocena. Pokazujemy słowa i zwroty z listy w metodyce, które mówca powtórzył co najmniej dwa razy.";

/** Ile slow widac bez rozwijania listy. */
export const DOMYSLNIE_WIDOCZNYCH_SLOW = 3;

/** Ile wypowiedzi ze slowem widac po jego rozwinieciu, zanim trzeba kliknac "Pokaz wszystkie". */
export const DOMYSLNIE_WIDOCZNYCH_WYPOWIEDZI = 5;

/** Przewija transkrypt do wypowiedzi o tym numerze (od 1) i zaznacza w niej podane trafienia slowa. */
export type PokazWypowiedzZeSlowem = (numer: number, trafienia: readonly ZakresTrafienia[]) => void;

function Kontekst({ wystapienie }: { wystapienie: WystapienieSlowa }): React.JSX.Element {
  // Trzy kropki ze zwyklych kropek (konwencja repo: bez znaku wielokropka), tylko tam, gdzie wypowiedz byla dluzsza.
  return (
    <span className="powtarzane-slowa__kontekst">
      {wystapienie.ucietyPoczatek && "..."}
      {wystapienie.przed}
      <strong>{wystapienie.trafienie}</strong>
      {wystapienie.po}
      {wystapienie.ucietyKoniec && "..."}
    </span>
  );
}

function PozycjaSlowa({
  slowo,
  onPokaz,
}: {
  slowo: PowtarzaneSlowo;
  onPokaz: PokazWypowiedzZeSlowem;
}): React.JSX.Element {
  const [otwarte, setOtwarte] = useState(false);
  const [wszystkieWypowiedzi, setWszystkieWypowiedzi] = useState(false);
  const idWystapien = useId();
  const idListyWypowiedzi = useId();
  const grupy = grupujPoWypowiedziach(slowo.wystapienia);
  const widoczne = wszystkieWypowiedzi ? grupy : grupy.slice(0, DOMYSLNIE_WIDOCZNYCH_WYPOWIEDZI);

  return (
    <li className="powtarzane-slowa__pozycja">
      <button
        type="button"
        className="powtarzane-slowa__przelacznik"
        aria-expanded={otwarte}
        aria-controls={idWystapien}
        onClick={() => setOtwarte((o) => !o)}
      >
        {/* Spacja miedzy slowem a plakietka jest dla nazwy przycisku ("„dobra” 5 razy"); w ukladzie flex nic nie zajmuje. */}
        <span className="powtarzane-slowa__slowo">{`„${slowo.slowo}”`}</span>{" "}
        <Badge>{`${slowo.liczba} ${odmien(slowo.liczba, "raz", "razy", "razy")}`}</Badge>
        <IconStrzalkaDol size={14} className="powtarzane-slowa__strzalka" />
      </button>
      <Collapse otwarty={otwarte} id={idWystapien}>
        <ul className="powtarzane-slowa__wystapienia" id={idListyWypowiedzi}>
          {widoczne.map((g) => (
            <li className="powtarzane-slowa__wystapienie" key={g.numer}>
              <button
                type="button"
                className="wskazanie__skok"
                onClick={() => onPokaz(g.numer, g.wystapienia.map(({ start, koniec }) => ({ start, koniec })))}
                aria-label={`Pokaż wypowiedź ${g.numer} w transkrypcie, słowo „${slowo.slowo}”${
                  g.wystapienia.length > 1 ? `, ${g.wystapienia.length} razy` : ""
                }`}
              >
                {`Wypowiedź ${g.numer}`}
              </button>
              <span className="powtarzane-slowa__konteksty">
                {g.wystapienia.map((w) => (
                  <Kontekst wystapienie={w} key={w.start} />
                ))}
              </span>
            </li>
          ))}
        </ul>
        {/* Przy pieciu i mniej wypowiedziach nie ma czego rozwijac: nie ma przycisku. */}
        {grupy.length > DOMYSLNIE_WIDOCZNYCH_WYPOWIEDZI && (
          <div className="powtarzane-slowa__wiecej">
            <button
              type="button"
              className="rozmowa-wiecej"
              aria-expanded={wszystkieWypowiedzi}
              aria-controls={idListyWypowiedzi}
              onClick={() => setWszystkieWypowiedzi((w) => !w)}
            >
              {wszystkieWypowiedzi
                ? "Pokaż mniej wypowiedzi"
                : `Pokaż wszystkie wypowiedzi z tym słowem (${grupy.length})`}
            </button>
          </div>
        )}
      </Collapse>
    </li>
  );
}

/** Lista powtarzanych slow jednego mowcy. Pusta lista nic nie rysuje: wtedy wolajacy nie pokazuje tez naglowka. */
export function ListaPowtarzanychSlow({
  slowa,
  onPokaz,
}: {
  slowa: readonly PowtarzaneSlowo[];
  /** Przewija transkrypt do wypowiedzi o tym numerze (od 1) i zaznacza w niej trafienia slowa. */
  onPokaz: PokazWypowiedzZeSlowem;
}): React.JSX.Element | null {
  const [wszystkie, setWszystkie] = useState(false);
  const idListy = useId();
  if (slowa.length === 0) return null;
  const widoczne = wszystkie ? slowa : slowa.slice(0, DOMYSLNIE_WIDOCZNYCH_SLOW);

  return (
    <>
      <ul className="powtarzane-slowa__lista" id={idListy}>
        {widoczne.map((s) => (
          <PozycjaSlowa slowo={s} onPokaz={onPokaz} key={s.slowo} />
        ))}
      </ul>
      {/* Przy trzech i mniej slowach nie ma czego rozwijac: nie ma przycisku. */}
      {slowa.length > DOMYSLNIE_WIDOCZNYCH_SLOW && (
        <button
          type="button"
          className="rozmowa-wiecej"
          aria-expanded={wszystkie}
          aria-controls={idListy}
          onClick={() => setWszystkie((w) => !w)}
        >
          {wszystkie ? "Pokaż mniej słów" : `Pokaż wszystkie słowa (${slowa.length})`}
        </button>
      )}
    </>
  );
}

/** Sekcja w karcie "Ocena wg metodyki", pod wskazaniami wybranego mowcy. Bez powtorzen jej nie ma. */
export function SekcjaPowtarzanychSlow({
  slowa,
  onPokaz,
  idNaglowka,
}: {
  slowa: readonly PowtarzaneSlowo[];
  onPokaz: PokazWypowiedzZeSlowem;
  idNaglowka: string;
}): React.JSX.Element | null {
  if (slowa.length === 0) return null;
  return (
    <section className="powtarzane-slowa" aria-labelledby={idNaglowka}>
      <h3 className="ocena-sekcja__tytul" id={idNaglowka}>
        Powtarzane słowa
      </h3>
      <p className="field-hint powtarzane-slowa__lead">{OBJASNIENIE_POWTARZANYCH_SLOW}</p>
      <ListaPowtarzanychSlow slowa={slowa} onPokaz={onPokaz} />
    </section>
  );
}
